import { serverUrl } from '../../../shared/utils/MkEnv'
import { postApi, fetchApi } from '../../../shared/api/MkApi'
import { MkSearchRequest, MkSearchResponse, MkSearchCalAvailRequest, MkSearchCalAvailResponse, MkResortAvailRequest, MkResortAvailResponse, MkSearchConstraintsResponse } from './searchTypes'
import { API_SEARCH_RESORT_LIST, API_SEARCH_CAL_AVAIL, API_SEARCH_CONSTRAINTS, API_SEARCH_RESORT_AVAIL, API_SEARCH_STAY_RULES, API_SEARCH_ALL_RESORT_AVAIL } from './MkSearchConstants'
import { formatDateAsISO } from '../../../shared/utils/dateUtils'

/**
 * Searches for resorts based on the search request
 * @param searchRequest - The search request payload
 * @param authHeaders - Optional authentication headers
 * @returns A promise that resolves to MkSearchResponse.
 */
export async function searchResorts(searchRequest: MkSearchRequest, authHeaders?: Record<string, string>): Promise<MkSearchResponse> {
  const url = `${serverUrl}${API_SEARCH_RESORT_LIST}`
  const body = JSON.stringify(searchRequest)
  return postApi<MkSearchResponse>(url, body, authHeaders)
}

/**
 * Searches availability calendar based on the search request
 * @param searchRequest - The search request payload
 * @param authHeaders - Optional authentication headers
 * @returns A promise that resolves to MkSearchCalAvailResponse.
 */
export async function searchCalAvail(searchRequest: MkSearchCalAvailRequest, authHeaders?: Record<string, string>): Promise<MkSearchCalAvailResponse> {
  const url = `${serverUrl}${API_SEARCH_CAL_AVAIL}`
  const body = JSON.stringify(searchRequest)
  return postApi<MkSearchCalAvailResponse>(url, body, authHeaders)
}

/**
 * GET /search/searchConstraints. Returns Voyager constraints (min/max nights, max check-in date) for date picker limits.
 * @param authHeaders - Required when logged in
 * @returns Response or null if 404/error
 */
export async function fetchSearchConstraints(authHeaders?: Record<string, string>): Promise<MkSearchConstraintsResponse | null> {
  const url = `${serverUrl}${API_SEARCH_CONSTRAINTS}`
  return fetchApi<MkSearchConstraintsResponse>(url, authHeaders ?? undefined)
}

/**
 * Creates a resort availability request object with the provided parameters
 *
 * @param params - Parameters for creating the resort availability request
 * @param params.productId - The product ID of the resort
 * @param params.checkInDate - The check-in date (Date object or ISO date string)
 * @param params.checkOutDate - The check-out date (Date object or ISO date string)
 * @param params.locationPath - Optional locationPath for location information
 * @param params.suiteType - Optional suite type filter
 * @param params.filters - Optional filters array or filter configuration
 * @param params.filters.includeAccessibleUnits - Whether to include accessible units (default: false)
 * @param params.filters.includeClubPassResorts - Whether to include club pass resorts (default: true)
 * @param params.sortBy - Optional sort order
 * @returns A properly formatted MkResortAvailRequest object
 */
export function createResortAvailRequest(params: {
  productId?: string
  checkInDate?: Date | string
  checkOutDate?: Date | string
  locationPath?: string
  suiteType?: string
  bonusTime?: boolean
  filters?:
    | {
        includeAccessibleUnits?: boolean
        includeClubPassResorts?: boolean
      }
    | Array<{ filterType: string; filterValues: string | boolean }>
  sortBy?: string
  sortOrder?: string
  oktaId?: string
  page?: string | number
  numberOfNights?: number
}): MkResortAvailRequest {
  // Format dates using formatDateAsISO with useUTC=true since dates stored in context
  // are UTC dates at midnight UTC representing the calendar date
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return ''
    if (typeof date === 'string') return date
    if (!(date instanceof Date)) return ''
    // Use formatDateAsISO with useUTC=true to extract UTC components directly
    // This avoids timezone conversion issues for UTC dates
    return formatDateAsISO(date, true)
  }

  // Build the request object
  const request: MkResortAvailRequest = {
    // Only include productId if it exists and is not "PI|null" or "null"
    ...(params.productId && params.productId !== 'null' && { productId: params.productId }),
    ...(params.locationPath && { locationPath: params.locationPath }),
    ...(params.suiteType && { suiteType: params.suiteType }),
    ...(params.sortBy && { sortBy: params.sortBy }),
    sortOrder: params.sortOrder || 'recommended',
    page: params.page || '1',
    ...(params.oktaId && { oktaId: params.oktaId }),
    ...(typeof params.bonusTime === 'boolean' && { bonusTime: params.bonusTime }),
  }

  // Only add date fields if they're not empty
  const checkInDateFormatted = formatDate(params.checkInDate)
  if (checkInDateFormatted) {
    request.checkInDate = checkInDateFormatted
  }

  const checkOutDateFormatted = formatDate(params.checkOutDate)
  if (checkOutDateFormatted) {
    request.checkOutDate = checkOutDateFormatted
  }

  // Only add numberOfNights if we're using flexible dates (which means we have a value > 0)
  if (params.numberOfNights && params.numberOfNights > 0) {
    request.numberOfNights = params.numberOfNights
  }

  // Handle filters
  if (params.filters) {
    if (Array.isArray(params.filters)) {
      request.filters = params.filters.map((filter) => ({
        filterType: filter.filterType,
        filterValues: filter.filterValues,
      }))
    } else {
      // Convert object format to array format
      const includeAccessibleUnits = params.filters.includeAccessibleUnits !== undefined ? params.filters.includeAccessibleUnits : false
      const includeClubPassResorts = params.filters.includeClubPassResorts !== undefined ? params.filters.includeClubPassResorts : true

      request.filters = [
        {
          filterType: 'include-accessable-units',
          filterValues: includeAccessibleUnits,
        },
        {
          filterType: 'include-clubpass-resorts',
          filterValues: includeClubPassResorts,
        },
      ]
    }
  }

  return request
}

/**
 * Fetches 90-day availability for one page of resorts from the backend.
 */
export async function fetchAllResortAvailPage(page: number, authHeaders?: Record<string, string>): Promise<Record<string, MkSearchCalAvailResponse>> {
  const url = `${serverUrl}${API_SEARCH_ALL_RESORT_AVAIL}?page=${page}`
  return fetchApi<Record<string, MkSearchCalAvailResponse>>(url, authHeaders)
}

/**
 * Searches resort availability for specific check-in and check-out dates
 * @param request - The resort availability request payload
 * @param authHeaders - Optional authentication headers
 * @returns A promise that resolves to MkResortAvailResponse.
 */
export async function searchResortAvail(request: MkResortAvailRequest, authHeaders?: Record<string, string>): Promise<MkResortAvailResponse> {
  const url = `${serverUrl}${API_SEARCH_RESORT_AVAIL}`
  const body = JSON.stringify(request)
  return postApi<MkResortAvailResponse>(url, body, authHeaders)
}

/**
 * Fetch stay rules (min/max length of stay) for a given check-in date and product.
 */
export async function fetchStayRulesApi(
  params: {
    checkInDate: string
    productId?: string
    unitTypes?: string[]
    membershipNumber?: string
    productOfferingIds?: string[]
  },
  authHeaders?: Record<string, string>,
): Promise<{
  minLengthOfStay?: number
  maxLengthOfStay?: number
  firstCheckoutDate?: string
  lastCheckoutDate?: string
} | null> {
  const url = `${serverUrl}${API_SEARCH_STAY_RULES}`
  const body = JSON.stringify({
    checkInDate: params.checkInDate,
    productId: params.productId,
    unitTypes: params.unitTypes,
    membershipNumber: params.membershipNumber,
    productOfferingIds: params.productOfferingIds,
  })

  return postApi<{
    minLengthOfStay?: number
    maxLengthOfStay?: number
    firstCheckoutDate?: string
    lastCheckoutDate?: string
  } | null>(url, body, authHeaders)
}
